import { getRouteApi, useNavigate } from '@tanstack/react-router';
import * as React from 'react';

import { ParticipationsClientProvider } from '@/api/participations-client-context';
import {
  DEFAULT_SUPPLIERS_VIEW,
  SuppliersListScreen,
  type SuppliersRegistryView,
} from '@/routes/admin/suppliers/suppliers-list-screen';

// =============================================================================
// suppliers-list-route.tsx — Adaptateur routeur du registre A-03 (T2-REGISTRE).
// =============================================================================
// Lie la navigation (TanStack Router) au composant de présentation (testable hors routeur). Ouvre la
// fiche A-04 (« /admin/suppliers/$siret ») ou l'écran d'enrôlement A-05 (« /admin/suppliers/new »).
//
// L'ÉTAT DE VUE (recherche/filtres/tri/pagination) vit dans les SEARCH PARAMS d'URL (RULE_REF_006 :
// PAS de localStorage pour l'état de vue) : deep-link + refresh-safe. L'adaptateur mappe search params
// ↔ SuppliersRegistryView et sérialise en retirant les DÉFAUTS (URL propre). Il AUTO-PORTE le DI seam
// du client « résumé participations » (ParticipationsClientProvider) sur le modèle du ClientsClientProvider.
//
// R22-FICHE-DRAWER (GATE_HUMAN, DBU §2.2 l.626/l.686) : « Consulter la fiche » n'est PLUS une navigation
// vers `/admin/suppliers/$siret` (page plein écran qui démontait le registre) — c'est un search param
// `open` sur CETTE MÊME route (`/admin/suppliers?open=<siret>`), lu par SuppliersListScreen pour ouvrir
// le volet latéral SANS démonter le registre. La route `/admin/suppliers/$siret` (supplier-detail-route)
// reste branchée telle quelle pour le deep-link direct (ex. lien externe, favori) — hors périmètre R22.

const routeApi = getRouteApi('/admin/suppliers');

export function SuppliersListRoute(): React.JSX.Element {
  const navigate = useNavigate();
  const search = routeApi.useSearch();

  // Mémoïsé : référencé par la dépendance de `onCloseDetail` (cf. infra) — évite de recréer ce callback
  // (et de casser sa propre mémoïsation) à CHAQUE rendu alors que seuls les search params en jeu changent.
  const view: SuppliersRegistryView = React.useMemo(
    () => ({
      q: search.q ?? DEFAULT_SUPPLIERS_VIEW.q,
      states: search.state ?? DEFAULT_SUPPLIERS_VIEW.states,
      tokenStatuses: search.tokenStatus ?? DEFAULT_SUPPLIERS_VIEW.tokenStatuses,
      from: search.from ?? DEFAULT_SUPPLIERS_VIEW.from,
      to: search.to ?? DEFAULT_SUPPLIERS_VIEW.to,
      campaignId: search.campaign ?? DEFAULT_SUPPLIERS_VIEW.campaignId,
      sort: search.sort ?? DEFAULT_SUPPLIERS_VIEW.sort,
      dir: search.dir ?? DEFAULT_SUPPLIERS_VIEW.dir,
      page: search.page ?? DEFAULT_SUPPLIERS_VIEW.page,
      pageSize: search.pageSize ?? DEFAULT_SUPPLIERS_VIEW.pageSize,
    }),
    [
      search.q,
      search.state,
      search.tokenStatus,
      search.from,
      search.to,
      search.campaign,
      search.sort,
      search.dir,
      search.page,
      search.pageSize,
    ],
  );

  const onChangeView = React.useCallback(
    (next: SuppliersRegistryView): void => {
      // On ne sérialise QUE les valeurs non-défaut : URL propre + rétro-compatible. Le param `open`
      // (volet latéral, R22-FICHE-DRAWER) N'EST JAMAIS reporté ici — changer un filtre pendant que le
      // volet est ouvert n'est normalement pas atteignable (overlay plein écran), et cette absence
      // délibérée est réutilisée telle quelle par `onCloseDetail` pour fermer le volet (cf. infra).
      void navigate({
        to: '/admin/suppliers',
        search: {
          ...(next.q !== '' ? { q: next.q } : {}),
          ...(next.states.length > 0 ? { state: [...next.states] } : {}),
          ...(next.tokenStatuses.length > 0
            ? { tokenStatus: [...next.tokenStatuses] }
            : {}),
          ...(next.from !== '' ? { from: next.from } : {}),
          ...(next.to !== '' ? { to: next.to } : {}),
          ...(next.campaignId !== '' ? { campaign: next.campaignId } : {}),
          ...(next.sort !== DEFAULT_SUPPLIERS_VIEW.sort ? { sort: next.sort } : {}),
          ...(next.dir !== DEFAULT_SUPPLIERS_VIEW.dir ? { dir: next.dir } : {}),
          ...(next.page > 1 ? { page: next.page } : {}),
          ...(next.pageSize !== DEFAULT_SUPPLIERS_VIEW.pageSize
            ? { pageSize: next.pageSize }
            : {}),
        },
      });
    },
    [navigate],
  );

  // R22-FICHE-DRAWER : ouvre le volet latéral EN PLACE (search param `open`), aucune navigation
  // vers une autre route — le registre reste monté. Conserve les autres search params courants
  // (filtres/tri/pagination inchangés pendant la consultation de la fiche).
  const onOpen = React.useCallback(
    (siret: string): void => {
      void navigate({ to: '/admin/suppliers', search: { ...search, open: siret } });
    },
    [navigate, search],
  );

  // Ferme le volet latéral : réutilise la sérialisation de `onChangeView` (qui ne porte JAMAIS `open`,
  // cf. supra) sur la vue COURANTE — reconstruit l'URL des filtres/tri/pagination à l'identique, sans
  // le param `open`. Évite une assignation `open: undefined` (incompatible avec
  // `exactOptionalPropertyTypes`, tsconfig.base.json) ou une déstructuration à variable ignorée.
  const onCloseDetail = React.useCallback((): void => {
    onChangeView(view);
  }, [onChangeView, view]);

  const onEnroll = React.useCallback((): void => {
    void navigate({ to: '/admin/suppliers/new' });
  }, [navigate]);

  // T7-MULTI-ETAB : ouvre la vue groupée des établissements d'un SIREN (segment top-level distinct).
  const onOpenHierarchy = React.useCallback(
    (siren: string): void => {
      void navigate({ to: '/admin/legal-entities/$siren', params: { siren } });
    },
    [navigate],
  );

  return (
    <ParticipationsClientProvider>
      <SuppliersListScreen
        onOpen={onOpen}
        onEnroll={onEnroll}
        onOpenHierarchy={onOpenHierarchy}
        view={view}
        onChangeView={onChangeView}
        openSiret={search.open ?? null}
        onCloseDetail={onCloseDetail}
      />
    </ParticipationsClientProvider>
  );
}
